import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';

import { useHttpClient } from '@cccom/shared/data-access';

import { Site } from '../types';

const prefix = '/system-inventory/sites';

export const useSites = () => {
  const client = useHttpClient();

  const getSites = async () =>
    (
      await client.get<Site[]>(`${prefix}`, {
        ccOptions: { skipSite: true },
      })
    ).data;

  const query = useQuery<Site[]>({
    queryKey: ['sites'],
    queryFn: getSites,
    staleTime: Infinity,
  });

  return query;
};

type CurrentSite = {
  site: Site;
  autoSelected: boolean;
};

export const useCurrentSite = () => {
  const client = useHttpClient();

  const getCurrentSite = async () =>
    (
      await client.get<CurrentSite>(`${prefix}/current`, {
        ccOptions: { skipSite: true },
      })
    ).data;

  const query = useQuery<CurrentSite, Error, CurrentSite>({
    queryKey: ['currentSite'],
    staleTime: Infinity,
    queryFn: getCurrentSite,
  });

  return query;
};

export const useSetCurrentSite = () => {
  const [, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const client = useHttpClient();

  const setCurrentSite = async (siteId: string) =>
    (await client.put(`${prefix}/${siteId}/current`)).data;

  const mutation = useMutation({
    mutationFn: setCurrentSite,
    onSuccess: () => {
      // Invalidate and refetch current site data
      queryClient.invalidateQueries({ queryKey: ['currentSite'] });
      setSearchParams({});
    },
  });

  return mutation;
};
